// A replacement reply with no return label (Jenn, 2026-11-03,
// docs/decisions/2026-11-03-replacement-label-box-only.md): a $0 RMA code
// (other than 70 and 92) keeps its "Replace Company Pays" template, with its
// return label lines taken out when the step's $0 RMA label box is not
// checked. The lines are named per template variant (the template's key and
// its folder), word for word as the live templates read on 2026-11-03. A
// variant whose lines are not all found exactly (the template was edited, or
// a folder not listed here) is left whole, with the review reason below, so
// nothing is guessed.
//
// Pure: no calls.

export const REPLACEMENT_LABEL_LINES_REVIEW_REASON = 'Remove the return label lines before sending'

// The template key these lines belong to.
export const REPLACEMENT_LABEL_LINES_KEY = 'replace_company_pays'

const EBAY_LINES = Object.freeze([
  'Returning the Original',
  "We've also emailed a prepaid shipping label. Click the link in the email to retrieve your label, select the mobile code option, and show it to any UPS Store or authorized UPS shipping location — they'll scan it and ship the boxed part(s).",
])
const WEBSITE_STEPS = Object.freeze([
  "Check Email — Free UPS label sent to your inbox (check promotions and junk if you don't see it)",
  'Pack — Box up the parts.',
  'Print or Scan — Print the label yourself or UPS can print at their store with the mobile code.',
])

// folder -> the lines to take out, each compared after normalizeLine().
export const REPLACEMENT_LABEL_LINES = Object.freeze({
  '1A eBay/Replace': EBAY_LINES,
  'AM eBay/Replace': EBAY_LINES,
  '1A Website/Replace': Object.freeze(["Here's what to next:", ...WEBSITE_STEPS]),
  'AM Website/Replace': Object.freeze(["Here's what to do next:", ...WEBSITE_STEPS]),
})

// A line as compared: tags out, &nbsp; and runs of whitespace as one space,
// curly apostrophes straight, trimmed.
export function normalizeLine(value) {
  return String(value ?? '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;| /g, ' ')
    .replace(/&#39;|&rsquo;|&lsquo;|[‘’]/g, "'")
    .replace(/&amp;/g, '&')
    .replace(/&mdash;/g, '—')
    .replace(/\s+/g, ' ')
    .replace(/\s+([.,:;)])/g, '$1')
    .trim()
}

function isHtml(text) {
  return /<p[\s>]/i.test(text)
}

// The text's blocks: an HTML text's <p> paragraphs, a plain text's lines.
function blocksOf(text) {
  if (isHtml(text)) {
    const out = []
    const re = /<p\b[^>]*>[\s\S]*?<\/p>/gi
    let m
    while ((m = re.exec(text))) out.push({ start: m.index, end: m.index + m[0].length, text: m[0] })
    return out
  }
  const out = []
  let at = 0
  for (const line of text.split('\n')) {
    out.push({ start: at, end: at + line.length, text: line })
    at += line.length + 1
  }
  return out
}

// The reply without the variant's label lines: { text, removed, certain }.
// removed: the lines taken out, as the template reads (normalized).
// certain: false when the folder has no listed lines or any listed line is
// missing; the text is then returned whole.
export function withoutReplacementLabelLines(text, { folder } = {}) {
  const raw = String(text ?? '')
  const lines = REPLACEMENT_LABEL_LINES[String(folder ?? '')]
  if (!lines) return { text: raw, removed: [], certain: false }
  const blocks = blocksOf(raw)
  const hits = []
  for (const line of lines) {
    const block = blocks.find((b) => !hits.includes(b) && normalizeLine(b.text) === line)
    if (!block) return { text: raw, removed: [], certain: false }
    hits.push(block)
  }
  let out = raw
  for (const b of [...hits].sort((x, y) => y.start - x.start)) {
    out = out.slice(0, b.start) + out.slice(b.end + (isHtml(raw) ? 0 : (out[b.end] === '\n' ? 1 : 0)))
  }
  if (!isHtml(raw)) out = out.replace(/\n{3,}/g, '\n\n').replace(/\n+$/, raw.endsWith('\n') ? '\n' : '')
  return { text: out, removed: [...lines], certain: true }
}

// Every known label line found in a reply, taken out (the agent's reply,
// which may carry only some of them, 2026-11-03). { text, removed }.
export function withoutAnyReplacementLabelLines(text) {
  const raw = String(text ?? '')
  const known = new Set(Object.values(REPLACEMENT_LABEL_LINES).flat())
  const blocks = blocksOf(raw).filter((b) => known.has(normalizeLine(b.text)))
  if (!blocks.length) return { text: raw, removed: [] }
  let out = raw
  for (const b of [...blocks].sort((x, y) => y.start - x.start)) {
    out = out.slice(0, b.start) + out.slice(b.end + (isHtml(raw) ? 0 : (out[b.end] === '\n' ? 1 : 0)))
  }
  if (!isHtml(raw)) out = out.replace(/\n{3,}/g, '\n\n').replace(/\n+$/, raw.endsWith('\n') ? '\n' : '')
  return { text: out, removed: blocks.map((b) => normalizeLine(b.text)) }
}
